<template>
  <div>
    <template v-if="item">
      <DetailHeader :item="item" />
      <ElementRow class="detail-style__content">
        <ElementCol :span="4">
          <div class="detail-content__header">
            <StyleComments
              v-if="item"
              :item="item"
            />
          </div>
          <div class="detail-content__header attached">
            <p class="detail-content__header-title">
              Style details
              <span v-if="!isVendorUser">
                <i
                  class="el-icon-edit"
                  title="Edit style"
                  @click="openEditStyleForm"
                />
                <i
                  class="el-icon-delete"
                  title="Delete style"
                  @click="deleteStyle"
                />
              </span>
            </p>
          </div>
          <div class="detail-content__info">
            <DetailAttributes :item="item" />
          </div>
        </ElementCol>
        <ElementCol :span="20">
          <div class="detail-content__main-color-run">
            <div class="detail-content__color-run">
              <StitchColorRun :images="itemColorRun" />
            </div>
          </div>
          <ElementRow>
            <ElementCol :span="8">
              <div class="detail-content__versions">
                <DetailVersionList
                  :active-version="activeVersion"
                  :versions="versions"
                  :style-status="item.assetStatus"
                  @add-new-version="openVersionCreateForm"
                  @select-version="handleSelectVersion"
                  @rename-version="openVersionRenameForm"
                  @delete-version="deleteVersionAndShowConfirmation"
                  @rerender-version="rerenderVersionAndShowConfirmation"
                  @set-main-version="setStyleMainVersion"
                  @trigger-download="triggerDownloadFile"
                  @poll-versions-render-status="handleVersionPolling"
                />
              </div>
            </ElementCol>
            <ElementCol :span="16">
              <div class="detail-content__options">
                <div
                  v-if="item.versions.length === 0"
                  class="detail-content__no-versions"
                >
                  <h2>There is no versions yet.</h2>
                  <p>Please create a new version to see images.</p>
                  <p>
                    Need help? Email us at
                    <a href="mailto:3Dsupport@pvh.com">3Dsupport@pvh.com</a>.
                  </p>
                </div>
                <OptionList
                  v-else-if="activeVersion"
                  :active-version="activeVersion"
                  :style-status="item.assetStatus"
                  :options="options"
                  @add-option="handleCreateOption"
                  @update-option="handleUpdateOption"
                  @delete-option="handleDeleteOption"
                  @add-option-images="handleAddOptionImages"
                  @update-option-image="handleUpdateOptionImage"
                  @delete-option-image="handleDeleteOptionImage"
                />
              </div>
            </ElementCol>
          </ElementRow>
        </ElementCol>
      </ElementRow>

      <!-- Update item form -->
      <FormGenerator
        ref="editStyleForm"
        :form-configuration="styleFormConfiguration"
        :options="formFilters"
        :original-data="formOriginalData"
        :show-form-generator="showEditStyleForm"
        :specific-step-id-to-edit="specificStepIdToEdit"
        @form-close="closeEditStyleForm"
        @form-complete="completeEditStyleForm"
        @execute-trigger="executeTrigger"
      />

      <!-- Create style version form -->
      <FormGenerator
        ref="createVersionForm"
        :additional-data="additionalVersionFormData"
        :default-data="defaultVersionFormData"
        :form-configuration="versionCreateFormConfiguration"
        :options="formFilters"
        :show-form-generator="showVersionCreateForm"
        @form-close="closeVersionCreateForm"
        @form-complete="completeVersionCreateForm"
        @execute-trigger="executeTrigger"
      />

      <!-- Rename style version form -->
      <FormGenerator
        ref="renameVersionForm"
        :additional-data="additionalVersionFormData"
        :form-configuration="versionRenameFormConfiguration"
        :original-data="defaultVersionFormData"
        :show-form-generator="showVersionRenameForm"
        @form-close="closeVersionRenameForm"
        @form-complete="completeVersionRenameForm"
      />

      <!-- Create/Update Option form -->
      <FormGenerator
        ref="optionForm"
        :additional-data="additionalOptionFormData"
        :form-configuration="optionFormConfiguration"
        :options="{ formPlmOptions, ...formFilters }"
        :original-data="defaultOptionFormData"
        :show-form-generator="showOptionForm"
        :specific-step-id-to-edit="specificStepIdToEdit"
        @form-close="closeOptionForm"
        @form-complete="completeOptionForm"
      />
    </template>
    <template v-else-if="isLoadingItem">
      <div class="detail-style__body-loader">
        <StitchLoader />
      </div>
    </template>
    <LibraryDetailError
      v-if="errorStatus"
      :active-library="libraryType"
      :error-status="errorStatus"
    />
  </div>
</template>

<script>
import { FORM_NAME } from '@/constants/formName'
import { mapActions, mapGetters } from 'vuex'
import {
  convertObjectKeysToCamelCase,
  formatToLocaleDateShort
} from '@/services/utils'
import { getFormConfig } from '@/services/formUtils'
import { LIBRARY_TYPE } from '@/constants/libraryType'
import { JOB_STATUS } from '@/constants/loadingStatus'
import { TRACKER_OBJECTS, TRACKER_EVENTS } from '@/constants/tracker'
import DetailHeader from './components/DetailHeader.vue'
import DetailAttributes from './components/DetailAttributes.vue'
import DetailVersionList from './components/DetailVersionList'
import LibraryDetailError from '../LibraryDetailError'
import OptionList from '@/components/option/OptionList'

// triggerDownload
import { DataUtils } from '@/mixins/utils.js'
import VueTypes from 'vue-types'
import { FILTER_TYPE } from '@/constants/filterType'
import FeatureFlags from '@/services/featureFlags'
import placeholder360 from '@/assets/images/placeholder360.png'
import { IMAGE_RESOLUTION, IMAGE_VIEW_ANGLE } from '@/constants/image'
import { USER_ROLE } from '@/constants/roleType'
import { ICON_TYPE, STYLE_TYPE } from '@/constants/dropdownOptions'
import { ASSET_STATUS } from '@/constants/assetStatuses'

export default {
  name: 'DetailStyle',

  components: {
    OptionList,
    DetailHeader,
    DetailAttributes,
    DetailVersionList,
    LibraryDetailError
  },

  mixins: [DataUtils],

  props: {
    itemId: VueTypes.number.isRequired
  },

  emits: ['close'],

  data () {
    return {
      productResponsibleUsers: [],
      digitalApproverUsers: [],
      isLoadingItem: false,
      activeVersion: null,
      showEditStyleForm: false,
      specificStepIdToEdit: null,
      defaultDataForUpdate: null,
      defaultVersionFormData: null,
      defaultOptionFormData: null,
      showVersionRenameForm: false,
      showVersionCreateForm: false,
      showOptionForm: false,
      formStepColorway: {
        MANUAL: 'colorwayManual',
        EDIT: 'colorwayEdit',
        PLM: 'colorwayPlm'
      },
      formStepStyle: {
        DETAILS: 'details'
      },
      errorStatus: null,

      LIBRARY_TYPE,
      ICON_TYPE,
      ASSET_STATUS,
      USER_ROLE,
      watchState: false,
      isTogglingWatch: false,
      isLoadingStatus: false,
      isLoadingVendor: false,
      isLoadingQualityCheck: false,
      showStyleStatusActionForm: false,
      styleStatusActionDefaultFormData: null,
      styleStatusActionReasons: {},
      styleQCActiveColor: '#69db8d',
      styleQCInactiveColor: '#f56c6c',
      styleQualityCheckStatus: null,
      disabledMessage:
        'As a vendor user, you are not allowed to perform this action.',
      editableResponsibleProduct: {
        id: null,
        email: null,
        edit: false
      },
      editableSubmissionDeadline: {
        deadline: null,
        edit: false
      },
      datePickerOptions: {
        /**
         * Disable any date before tomorrow.
         *
         * @param   {Date}    date - The date being evaluated by the picker.
         * @returns {boolean}      - `true` if the date should be disabled (i.e. is before tomorrow).
         */
        disabledDate (date) {
          const today = new Date()
          const tomorrow = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() + 1
          )

          return date < tomorrow
        }
      },
      showAllTags: false,
      maxVisibleTags: 2
    }
  },

  computed: {
    ...mapGetters([
      'getItemDetail',
      'getMainVersionFromActiveStyle',
      'getVersionById',
      'getOptionsByVersionId',
      'getAvailableFilters',
      'getOptionById',
      'getPlmOptions',
      'getVersionsUploadInProgress',
      'getTags'
    ]),

    ...mapGetters({
      user: 'getCognitoUserData'
    }),

    /**
     * @returns {object}
     */
    item () {
      return this.getItemDetail()
    },

    /**
     * @returns {object}
     */
    itemColorRun () {
      return this.getColorRunResized(this.item, IMAGE_RESOLUTION.THUMBNAIL)
    },

    /**
     * @returns {string}
     */
    createdInfoFormatted () {
      if (this.item.contributor) {
        return `${this.item.contributor} on ${formatToLocaleDateShort(
          this.item.createdAt
        )}`
      }

      return formatToLocaleDateShort(this.item.createdAt)
    },

    /**
     * @returns {boolean}
     */
    canAccessRenderEngineChoice () {
      return FeatureFlags.canTeamAccessRenderEngineChoice()
    },

    /**
     * @returns {boolean}
     */
    isVendorUser () {
      return this.user.role === USER_ROLE.VENDOR.name
    },

    /**
     * @returns {Array}
     */
    dropdownItems () {
      return [
        {
          name: 'Edit Information'
          // method: () => this.emitEditItem()
        },
        {
          name: `Delete ${this.libraryType}`,
          styles: [STYLE_TYPE.DANGER]
          // method: () => this.emitDeleteItem()
        }
      ]
    },

    /**
     * @returns {object} OriginalData
     */
    formOriginalData () {
      // this is meant to normalize attributes for the FormGenerator
      const { attributes, ...formOriginalData } = {
        ...this.item,
        ...this.item.attributes
      }

      // for vendors, company is always added by default
      // the role is used to distinguish between users
      // inside the FormGenerator
      if (this.isVendorUser) {
        formOriginalData.vendorCompanyId =
          this.item.vendorCompanyId ?? this.user.vendor_company.id
        formOriginalData.role = USER_ROLE.VENDOR.name
      } else if (this.item.vendorCompanyId) {
        formOriginalData.role = this.user.role
      }

      formOriginalData.submissionDeadlineDate =
        this.item.submissionDeadline.deadline

      return formOriginalData
    },

    /**
     * @returns {Array}
     */
    pendingVersions () {
      return this.getVersionsUploadInProgress[this.itemId]
    },

    /**
     * @returns {Array}
     */
    versions () {
      if (!this.getItemDetail()) {
        // early return if getItemDetail is empty (when fetchItemDetail within created() hasn't been resolved yet)
        return
      }

      const versions = [...this.getItemDetail().versions]

      if (this.pendingVersions) {
        this.pendingVersions.forEach(pendingVersion => {
          if (pendingVersion.isMain) {
            return versions.unshift(pendingVersion)
          }

          return versions.push(pendingVersion)
        })
      }

      return versions
    },

    /**
     * @returns {object}
     */
    mainVersion () {
      return this.getMainVersionFromActiveStyle
    },

    /**
     * @returns {Array}
     */
    options () {
      const { id } = this.activeVersion
      const options = this.getOptionsByVersionId(id).map(option => {
        // create a new array for images to avoid mutating Vuex state
        let newImages = [...option.images]

        // if there are any slideshow images,
        // add a placeholder for the slideshow
        if (option.slideshowImages.length > 0) {
          const hasView360Placeholder = newImages.some(
            img => img.view_name === IMAGE_VIEW_ANGLE.VIEW360
          )

          if (!hasView360Placeholder) {
            const view360Placeholder = {
              id: Math.ceil(Math.random() * 1000000),
              is_thumbnail: false,
              view_name: IMAGE_VIEW_ANGLE.VIEW360,
              file_url: placeholder360
            }
            // Create a new array with the placeholder at the beginning
            newImages = [view360Placeholder, ...newImages]
          }
        }

        // Return a new object with the updated images array
        return {
          ...option,
          images: newImages
        }
      })

      return options
    },

    /**
     * @returns {Array}
     */
    formFilters () {
      if (this.canAccessRenderEngineChoice) {
        return convertObjectKeysToCamelCase({
          ...FILTER_TYPE.RENDER_ENGINE_FILTERS,
          ...this.getAvailableFilters(),
          responsible_product_id: this.responsibleProductUsers,
          digital_approver_id: this.digitalApproverUsers,
          tags: this.getTags()
        })
      }

      return convertObjectKeysToCamelCase({
        ...this.getAvailableFilters(),
        responsible_product_id: this.responsibleProductUsers,
        digital_approver_id: this.digitalApproverUsers,
        tags: this.getTags()
      })
    },

    /**
     * @returns {Array}
     */
    formPlmOptions () {
      const plmOptions = this.getPlmOptions()

      if (!this.item.plmCode || !plmOptions) {
        return []
      }

      return plmOptions.map(opt => {
        if (typeof opt === 'string') {
          // if detailed is false
          return {
            name: opt,
            id: opt
          }
        }

        // if detailed is true
        const code = opt.code || ''
        const name = opt.name || ''
        const label = code && name ? `${code} (${name})` : code || name

        return {
          name: label,
          id: code
        }
      })
    },

    /**
     * @returns {object}
     */
    styleFormConfiguration () {
      const config = getFormConfig(this.libraryType)

      if (this.isVendorUser) {
        config.steps[1].fields[5].visible = false
      }

      return config
    },

    /**
     * @returns {object}
     */
    additionalVersionFormData () {
      return {
        groupId: this.item.groupId,
        styleId: this.itemId,
        versionId:
          (this.defaultVersionFormData && this.defaultVersionFormData.id) ||
          undefined
      }
    },

    /**
     * @returns {object}
     */
    additionalOptionFormData () {
      return {
        styleId: this.itemId,
        versionId: (this.activeVersion && this.activeVersion.id) || undefined,
        optionId:
          (this.defaultOptionFormData && this.defaultOptionFormData.optionId) ||
          undefined
      }
    },

    /**
     * @returns {object}
     */
    versionCreateFormConfiguration () {
      const config = getFormConfig(FORM_NAME.VERSION_CREATE)
      config.steps[0].fields[1].visible = this.canAccessRenderEngineChoice

      return config
    },

    /**
     * @returns {object}
     */
    versionRenameFormConfiguration () {
      return getFormConfig(FORM_NAME.VERSION_RENAME)
    },

    /**
     * @returns {object}
     */
    optionFormConfiguration () {
      return getFormConfig(FORM_NAME.OPTION_CREATE)
    },

    /**
     * @returns {string}
     */
    libraryType () {
      return LIBRARY_TYPE.STYLE
    }
  },

  watch: {
    /**
     * @param {Array} value
     */
    versions (value) {
      // force a rerender of the activeVersion once the fake version gets replaced by the real one
      if (this.activeVersion && this.activeVersion.fakeId) {
        const replacedVersion = value.find(
          version => version.name === this.activeVersion.name
        )

        this.activeVersion = replacedVersion || this.activeVersion
      }
    }
  },

  async created () {
    if (this.itemId !== null) {
      this.isLoadingItem = true

      try {
        await this.fetchItemDetail({
          itemId: this.itemId,
          libraryType: this.libraryType
        })

        await this.initStatuses()

        // This is a fix to avoid using global team users list
        // If user goes to "All", these lists contain user from multiple teams
        if (this.item?.groupId && !this.isVendorUser) {
          const responsibleProducts =
            await this.fetchResponsibleProductUsersNoSave({
              teamId: this.item.groupId
            })
          const digitalApprovers = await this.fetchDigitalApproverUsersNoSave({
            teamId: this.item.groupId
          })
          this.responsibleProductUsers = responsibleProducts.map(user => ({
            ...user,
            label: user.email,
            name: user.email
          }))
          this.digitalApproverUsers = digitalApprovers.map(user => ({
            ...user,
            label: user.email,
            name: user.email
          }))
        }
      } catch (error) {
        if (error.response) {
          this.errorStatus = error.response.status
        } else {
          throw error
        }
      }

      if (this.mainVersion) {
        this.activeVersion = this.mainVersion
      }

      this.isLoadingItem = false
    }
  },

  beforeDestroy () {
    this.clearItemDetail({ libraryType: this.libraryType })
  },

  methods: {
    ...mapActions([
      'fetchItemDetail',
      'fetchItemAssetStatus',
      'fetchTags',
      'clearItemDetail',
      'deleteItem',
      'deleteStyleVersion',
      'rerenderStyleVersion',
      'fetchPlmOptions',
      'fetchJobDetailWithTasks',
      'fetchVersionRenderStatus',
      'setMainVersion',
      'deleteOption',
      'updateStyleOptionImage',
      'deleteStyleOptionImage',
      'addStyleOptionImages',
      'fetchResponsibleProductUsersNoSave',
      'fetchDigitalApproverUsersNoSave'
    ]),

    /**
     * @param {string} versionsId
     */
    async handleVersionPolling (versionsId) {
      const statusDetails = await this.fetchVersionRenderStatus(versionsId)
      const startedJob = statusDetails.find(
        detail => detail.status === JOB_STATUS.STARTED
      )

      if (startedJob) {
        const renderingVersion = this.versions.find(
          version => version.renderJobId === startedJob.id
        )

        this.fetchJobDetailWithTasks({
          jobId: startedJob.id,
          versionId: renderingVersion.id
        })
      }
    },

    /**
     *
     */
    async initStatuses () {
      await this.fetchItemAssetStatus({
        itemId: this.item.id,
        libraryType: this.libraryType
      })
    },

    /**
     * @param {object} payload
     */
    handleSelectVersion (payload) {
      const { version } = payload

      this.activeVersion = version

      this.$tracking.trackEvent({
        object: TRACKER_OBJECTS.STYLE_VERSION,
        action: TRACKER_EVENTS.VIEWED,
        category: this.libraryType,
        item: version,
        value: version.id
      })
    },

    /**
     * @param {object} payload
     */
    setStyleMainVersion (payload) {
      this.setMainVersion(payload)
    },

    /**
     */
    async handleCreateOption () {
      const plmCode = this.item.plmCode
      this.defaultOptionFormData = null

      if (plmCode) {
        this.defaultOptionFormData = {
          plmCode
        }

        const plmOptions = await this.fetchPlmOptions({
          styleId: this.itemId,
          versionId: this.activeVersion.id,
          onlyUnassigned: true,
          detailed: true
        })

        if (!plmOptions || plmOptions.length === 0) {
          this.$message.warning({
            message: 'No PLM colorways available for this version.',
            duration: 5000,
            showClose: true
          })

          return
        }
      } else {
        this.specificStepIdToEdit = this.formStepColorway.MANUAL
      }

      this.openOptionForm()
    },

    /**
     */
    openOptionForm () {
      this.fetchTags({
        groupId: this.item.groupId,
        seasonId: this.item.seasonId,
        isOptionForm: true
      })

      this.showOptionForm = true
    },

    /**
     * @param {object} payload
     */
    handleUpdateOption (payload) {
      const plmCode = this.item.plmCode
      const versionId = this.activeVersion.id
      const optionId = payload.optionId
      const option = this.getOptionById({
        versionId,
        optionId
      })

      this.specificStepIdToEdit = this.formStepColorway.EDIT

      this.defaultOptionFormData = {
        ...convertObjectKeysToCamelCase(option),
        plmCode,
        versionId,
        optionId
      }

      this.openOptionForm()
    },

    /**
     */
    closeOptionForm () {
      this.showOptionForm = false
      this.defaultOptionFormData = null
      this.specificStepIdToEdit = null
    },

    /**
     * @param {number} success
     */
    completeOptionForm (success) {
      if (success) {
        this.closeOptionForm()
      }
    },

    /**
     * @param {object} payload
     * @param {string} payload.url
     * @param {string} payload.object
     */
    triggerDownloadFile ({ url, object }) {
      this.triggerDownload(url)

      this.$tracking.trackEvent({
        object,
        action: TRACKER_EVENTS.DOWNLOADED,
        category: this.libraryType,
        label: url,
        value: this.activeVersion.id,
        item: this.activeVersion
      })
    },

    /**
     */
    closeEditStyleForm () {
      this.defaultDataForUpdate = null
      this.showEditStyleForm = false
    },

    /**
     */
    openEditStyleForm () {
      if (this.item.plmCode) {
        this.specificStepIdToEdit = this.formStepStyle.DETAILS
      } else {
        this.specificStepIdToEdit = null
      }

      this.showEditStyleForm = true
    },

    /**
     * @param {object} payload
     */
    openVersionCreateForm (payload) {
      if (payload) {
        this.defaultVersionFormData = {
          ...this.defaultVersionFormData,
          ...payload
        }
      }

      this.showVersionCreateForm = true
    },

    /**
     */
    closeVersionCreateForm () {
      this.defaultVersionFormData = null
      this.showVersionCreateForm = false
    },

    /**
     * @param {string} triggerMethodName
     * @param {object} triggerData
     */
    async executeTrigger (triggerMethodName, triggerData) {
      await this[triggerMethodName](triggerData)
    },

    /**
     * @param {object} triggerData
     */
    async formFetchTags (triggerData) {
      const { groupId, seasonId } = triggerData.processedItemData.new

      if (groupId && seasonId) {
        await this.fetchTags({
          groupId: groupId,
          seasonId: seasonId,
          isOptionForm: true
        })
      }
    },

    /**
     * @param {boolean} success
     */
    completeVersionCreateForm (success) {
      if (success) {
        this.closeVersionCreateForm()
      }
    },

    /**
     * @param {boolean} success
     */
    completeEditStyleForm (success) {
      if (success) {
        this.closeEditStyleForm()
      }
    },

    /**
     *
     */
    async deleteStyle () {
      return await this.$confirm(
        `This will permanently delete ${this.item.name}. Continue?`,
        'Warning',
        {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning',
          showClose: false
        }
      )
        .then(async () => {
          await this.deleteItem({
            id: this.item.id,
            name: this.item.name
          })

          this.goToStyles()
        })
        .catch(() => {})
    },

    /**
     */
    goToStyles () {
      this.$router.push({
        path: '/styles/',
        query: { group_id: this.$route.query.group_id }
      })
    },

    /**
     * @param {object} payload
     */
    openVersionRenameForm (payload) {
      this.defaultVersionFormData = {
        ...payload
      }
      this.showVersionRenameForm = true
    },

    /**
     * @param {number} success
     */
    completeVersionRenameForm (success) {
      if (success) {
        this.closeVersionRenameForm()
      }
    },

    /**
     */
    closeVersionRenameForm () {
      this.defaultVersionFormData = null
      this.showVersionRenameForm = false
    },

    /**
     * @param {object} payload
     */
    async deleteVersionAndShowConfirmation (payload) {
      await this.deleteStyleVersion(payload)

      this.activeVersion = this.mainVersion

      this.$message({
        showClose: true,
        message: `${payload.versionName} has been deleted`
      })
    },

    /**
     */
    async rerenderVersionAndShowConfirmation () {
      const styleId = this.itemId
      const versionId = this.activeVersion.id
      const versionName = this.activeVersion.name

      await this.rerenderStyleVersion({
        styleId,
        versionId
      })

      this.activeVersion = this.mainVersion

      this.$message({
        showClose: true,
        message: `Style Version "${versionName}" has been triggered for a re-render, please wait ⏳`
      })
    },

    /**
     * @param {object} payload
     */
    async handleDeleteOption (payload) {
      const styleId = this.itemId
      const versionId = this.activeVersion.id
      const { optionId, optionCode } = payload

      await this.deleteOption({
        styleId,
        versionId,
        optionId
      })

      this.$message({
        showClose: true,
        message: `${optionCode} has been deleted`
      })
    },

    /**
     * @param {object} payload
     */
    handleAddOptionImages (payload) {
      this.addStyleOptionImages(payload)
    },

    /**
     * @param {object} payload
     */
    handleUpdateOptionImage (payload) {
      this.updateStyleOptionImage(payload)
    },

    /**
     * @param {object} payload
     */
    handleDeleteOptionImage (payload) {
      this.deleteStyleOptionImage(payload)
    }
  }
}
</script>

<style lang="scss" scoped>
$color-run-height: spacing(24);
$versions-padding-bottom: 50vh;

.detail-style {
  /deep/ .el-dialog {
    max-width: spacing(190);
    margin: auto;
    padding: 0;
  }

  /deep/ .el-dialog__body {
    overflow-y: hidden;
  }
}

.detail-style__container {
  display: flex;
  gap: 2rem;
  border-top: $border-divider;
}

.detail-style__body {
  border-top: $border-divider;
}

.detail-style__properties {
  border-top: $border-divider;
}

.detail-style__body-loader {
  padding-top: percentage(25);
  padding-right: percentage(10);
}

.detail-style__content {
  padding-right: spacing(4);
}

.detail-content__info {
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: spacing(1);
  margin-top: 0;
  overflow-y: auto;
  background-color: $grey-ultra-light;
}

.detail-content__main-color-run {
  position: sticky;
  top: 0;
  z-index: spacing(1.25);
  display: flex;
  align-items: center;
  justify-content: center;
  height: spacing(31);
  margin: spacing(0.5) 0 spacing(0.5) spacing(0.5);
  background-color: $grey-ultra-light;
  border-radius: spacing(0.5);
}

.detail-content__header {
  margin: spacing(1);
  margin-top: spacing(0.5);
  padding: spacing(0.5) spacing(2);
  background-color: $grey-very-light;
  border-radius: spacing(0.5);
}

.detail-content__header-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: spacing(1.5) 0;
  color: $grey-dark;
  font-weight: $font-weight-medium;
  font-size: $font-size-label-medium;

  i {
    margin-left: spacing(0.5);
    color: $blue;
    font-size: spacing(2.2);
    cursor: pointer;
  }
}

.attached {
  margin: 0 spacing(1);
  border-radius: spacing(0.5) spacing(0.5) 0 0;
}

.detail-content__versions {
  height: 100vh;
  margin: spacing(0.5) 0 spacing(0.5) spacing(0.5);
  padding-bottom: $versions-padding-bottom;
  overflow-y: auto;
  scrollbar-gutter: stable;
}

.detail-content__options {
  height: 100vh;
  margin: spacing(0.5) 0;
  padding-bottom: percentage(70);
  overflow-y: auto;
}

.detail-content__color-run {
  @include flex-center;

  justify-content: center;
  width: percentage(100);
  height: 100%;

  > * {
    max-height: $color-run-height;
  }
}

.detail-content__no-versions {
  width: 100%;
  margin: percentage(20) spacing(1) spacing(1);
  padding: spacing(1);
  color: $grey-dark;
  text-align: center;
  word-break: break-word;

  > * {
    margin-bottom: spacing(1);

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: $blue;
  }
}

/* Chrome, Edge, and Safari */
.detail-content__versions::-webkit-scrollbar,
.detail-content__options::-webkit-scrollbar {
  width: spacing(0.5); /* Thin vertical scrollbar */
}

.detail-content__versions::-webkit-scrollbar-track,
.detail-content__options::-webkit-scrollbar-track {
  background: transparent;
}

.detail-content__versions::-webkit-scrollbar-thumb,
.detail-content__options::-webkit-scrollbar-thumb {
  background-color: $grey-very-light;
  border-radius: spacing(0.5);
  transition: background-color 0.1s ease; /* rounded corners */
}

.detail-content__versions::-webkit-scrollbar-thumb:hover,
.detail-content__options::-webkit-scrollbar-thumb:hover {
  background-color: $grey-light;
}
</style>
